import React, { useEffect } from 'react';
import { blue } from '@mui/material/colors';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import './App.css';
import { isProd } from './components/Common';
import { LoginContext, FakeLoginContext } from './components/UserContext';
import { ThemeProvider, useTheme } from './components/ThemeContext';
import Routes from './routes';
import { initForegroundMessaging } from './firebaseMessaging';
import {
  initNotificationClickListener,
  openNotificationDeeplink,
} from './notificationDeeplink';

const AppContent = () => {
  const { darkMode } = useTheme();

  useEffect(() => {
    const stopClickListener = initNotificationClickListener();
    const stopMessaging = initForegroundMessaging((payload) => {
      const notification = payload?.notification;
      const data = payload?.data;
      if (!notification || Notification.permission !== 'granted') {
        return;
      }
      if (document.visibilityState === 'visible') {
        const browserNotification = new Notification(
          notification.title || 'Safe Reaction',
          {
            body: notification.body || '',
            data: data || {},
          },
        );
        const deeplink = data?.deeplink;
        if (deeplink) {
          browserNotification.onclick = () => {
            window.focus();
            openNotificationDeeplink(deeplink);
            browserNotification.close();
          };
        }
      }
    });
    return () => {
      stopClickListener();
      stopMessaging();
    };
  }, []);

  // Create theme based on dark mode state
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      secondary: {
        main: blue[900],
      },
      primary: {
        main: darkMode ? '#7591f6' : '#29235c',
      },
      background: {
        default: darkMode ? '#000000' : '#f5f5f5',
        paper: darkMode ? '#0a0a0a' : '#ffffff',
      },
    },
    typography: {
      // Use the system font instead of the default Roboto font.
      fontFamily: ['"Lato"', 'sans-serif'].join(','),
    },
    props: {
      MuiPaper: {
        elevation: 2,
      },
      MuiCheckbox: {
        color: 'primary',
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          subtitle1: {
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            textDecoration: 'underline',
          },
          subtitle2: {
            marginTop: '1.5rem',
            marginBottom: '0.2rem',
            fontWeight: 'bold',
            textDecoration: 'underline',
          },
          body2: {
            marginBottom: '0.5rem',
            fontStyle: 'italic',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            margin: '0.5rem',
            padding: '0.8rem',
            border: darkMode ? '1px solid #424242' : '1px solid #e0e0e0',
            width: 'fit-content',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          sizeSmall: {
            whiteSpace: 'nowrap',
            padding: '0.3rem 1rem 0.3rem 0',
            maxWidth: '20rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      PrivateSwitchBase: {
        styleOverrides: {
          root: {
            padding: '0 0.5rem 0 0.5rem',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          selectMenu: {
            padding: '2px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingBottom: '2px',
            paddingTop: '2px',
            lineHeight: '1.2',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: '2px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            marginRight: '0.3rem',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            padding: '0.4rem 0.4rem',
            width: '10rem',
          },
        },
      },
    },
  });

  const context = isProd()
    ? (children) => <LoginContext>{children}</LoginContext>
    : (children) => <FakeLoginContext>{children}</FakeLoginContext>;

  return context(
    <MuiThemeProvider theme={theme}>
      <Routes />
    </MuiThemeProvider>,
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;
